import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Box,
  InputAdornment
} from '@mui/material';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InventoryIcon from '@mui/icons-material/Inventory';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useCatalogContext } from './CatalogContext';
import { useUpdateProductPrice } from '../../../../client/hooks/products/products.mutations';

const CatalogActionDialog = () => {
  const {
    actionDialogOpen,
    setActionDialogOpen,
    currentAction,
    selectedProducts,
    setSelectedProducts,
    setAlert,
    setMessage,
    setSeverity
  } = useCatalogContext();
  const { mutate: updateProductPrice } = useUpdateProductPrice();

  // --- Refs para setters do contexto ---
  const setMessageRef = useRef(setMessage);
  const setSeverityRef = useRef(setSeverity);
  const setAlertRef = useRef(setAlert);
  const setSelectedProductsRef = useRef(setSelectedProducts);
  const setActionDialogOpenRef = useRef(setActionDialogOpen);

  useEffect(() => { setMessageRef.current = setMessage; }, [setMessage]);
  useEffect(() => { setSeverityRef.current = setSeverity; }, [setSeverity]);
  useEffect(() => { setAlertRef.current = setAlert; }, [setAlert]);
  useEffect(() => { setSelectedProductsRef.current = setSelectedProducts; }, [setSelectedProducts]);
  useEffect(() => { setActionDialogOpenRef.current = setActionDialogOpen; }, [setActionDialogOpen]);

  const [batchCmvPrice, setBatchCmvPrice] = useState('');

  const handleClose = () => {
    setActionDialogOpen(false);
  };

  const handleConfirm = () => {
    if (currentAction === 'update_price') {
      if (!batchCmvPrice || isNaN(parseFloat(batchCmvPrice)) || parseFloat(batchCmvPrice) < 0) {
        setMessageRef.current && setMessageRef.current('Por favor, insira um valor de CMV válido');
        setSeverityRef.current && setSeverityRef.current('error');
        setAlertRef.current && setAlertRef.current(true);
        return;
      }
      const payload = selectedProducts.map(sku => ({ sku, cmv_price: parseFloat(batchCmvPrice.replace(',', '.')) }));
      const selectedCount = selectedProducts.length;
      updateProductPrice(
        { data: payload },
        {
          onSuccess: () => {
            setMessageRef.current && setMessageRef.current(`CMV atualizado com sucesso em ${selectedCount} produto(s)!`);
            setSeverityRef.current && setSeverityRef.current('success');
            setAlertRef.current && setAlertRef.current(true);
            setTimeout(() => {
              setSelectedProductsRef.current && setSelectedProductsRef.current([]);
              setBatchCmvPrice('');
              setActionDialogOpenRef.current && setActionDialogOpenRef.current(false);
            }, 300);
          },
          onError: (error) => {
            setMessageRef.current && setMessageRef.current(error?.response?.data?.message || 'Erro ao atualizar CMV');
            setSeverityRef.current && setSeverityRef.current('error');
            setAlertRef.current && setAlertRef.current(true);
          }
        }
      );
      return;
    } else {
      // Implementar ação conforme currentAction
      setMessageRef.current && setMessageRef.current(`Ação "${currentAction}" aplicada com sucesso em ${selectedProducts.length} produtos`);
      setSeverityRef.current && setSeverityRef.current('success');
      setAlertRef.current && setAlertRef.current(true);
      setTimeout(() => {
        setActionDialogOpenRef.current && setActionDialogOpenRef.current(false);
        setSelectedProductsRef.current && setSelectedProductsRef.current([]);
      }, 0);
    }
  };

  return (
    <Dialog
      open={actionDialogOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: 500
        }
      }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {currentAction === 'publish' && 'Publicar Produtos'}
        {currentAction === 'unpublish' && 'Despublicar Produtos'}
        {currentAction === 'update_price' && 'Atualizar CMV dos Produtos'}
        {currentAction === 'update_stock' && 'Atualizar Estoque'}
        {currentAction === 'delete' && 'Excluir Produtos'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {selectedProducts.length === 0 ? (
            'Nenhum produto selecionado. Selecione os produtos para continuar.'
          ) : currentAction === 'delete' ? (
            `Você está prestes a excluir ${selectedProducts.length} produto(s). Esta ação não pode ser desfeita.`
          ) : currentAction === 'update_price' ? (
            `Você está prestes a atualizar o CMV de ${selectedProducts.length} produto(s) selecionado(s). Informe o novo valor de CMV para aplicar.`
          ) : (
            `Você selecionou ${selectedProducts.length} produto(s) para esta ação.`
          )}
        </DialogContentText>

        {selectedProducts.length > 0 && currentAction === 'update_price' && (
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Novo valor de CMV"
              fullWidth
              margin="normal"
              size="small"
              value={batchCmvPrice}
              onChange={e => setBatchCmvPrice(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>
              }}
            />
          </Box>
        )}

        {selectedProducts.length > 0 && currentAction === 'update_stock' && (
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Quantidade"
              fullWidth
              margin="normal"
              size="small"
              type="number"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          sx={{ color: 'text.secondary' }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={currentAction === 'delete' ? 'error' : 'primary'}
          disabled={selectedProducts.length === 0}
          startIcon={
            currentAction === 'publish' ? <PublishIcon /> :
              currentAction === 'unpublish' ? <UnpublishedIcon /> :
                currentAction === 'update_price' ? <LocalOfferIcon /> :
                  currentAction === 'update_stock' ? <InventoryIcon /> :
                    currentAction === 'delete' ? <DeleteOutlineIcon /> : null
          }
        >
          {currentAction === 'delete' ? 'Excluir' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CatalogActionDialog;
